"use client";

import { useMediaContext } from "@/features/media/useMediaContext";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import {
  FALLBACK_COLOR,
  layerColors,
  mapIconUrl,
} from "@/features/layers/legendSwatches";
import { collectMapIcons } from "@/features/map/utils/images";
import type { StyleLayer } from "@/types";

/**
 * The mark that stands for one map layer in the legend: a bar for a line, a
 * square for a fill, a disc for a circle, the icon itself for a symbol.
 *
 * A layer painted in several colours shows all of them rather than picking one —
 * split into bands while they still read as bands, and blended into a gradient
 * once there are too many for that.
 */
export function LegendItem({
  styleLayers,
  children,
}: {
  styleLayers: StyleLayer[];
  children?: React.ReactNode;
}) {
  const { mapIcons, iconMapping } = useProjectConfigs();
  const media = useMediaContext();

  // The same list the map sprite is built from: a project's artwork usually
  // hangs off `iconMapping`, so reading `mapIcons` alone found none of it.
  const icons = collectMapIcons({ mapIcons, iconMapping });

  return (
    <div className="flex w-full items-center gap-2 overflow-hidden">
      <div className="flex shrink-0 items-center gap-2">
        {styleLayers.map((layer, index) => {
          const colors = layerColors(layer.paint);
          const key = `${layer.type}-${index}`;

          if (layer.type === "fill") return <Fill key={key} colors={colors} />;
          if (layer.type === "line") return <Line key={key} colors={colors} />;
          if (layer.type === "circle") return <Circle key={key} colors={colors} />;

          return (
            <Symbol
              key={key}
              layer={layer}
              color={colors[0] ?? FALLBACK_COLOR}
              iconUrl={mapIconUrl(layer.layout?.["icon-image"], icons, media)}
            />
          );
        })}
      </div>

      {children}
    </div>
  );
}

function Line({ colors }: { colors: string[] }) {
  if (colors.length === 1) {
    return (
      <div
        className="h-1 w-6 shrink-0 rounded"
        style={{ backgroundColor: colors[0] }}
      />
    );
  }

  if (colors.length <= 6) {
    return (
      <div className="flex h-1 w-6 shrink-0 overflow-hidden rounded">
        {colors.map((color, index) => (
          <div key={`${color}-${index}`} className="h-full flex-1" style={{ backgroundColor: color }} />
        ))}
      </div>
    );
  }

  return (
    <div
      className="h-1 w-6 shrink-0 rounded"
      style={{ background: `linear-gradient(to right, ${colors.join(", ")})` }}
    />
  );
}

function Fill({ colors }: { colors: string[] }) {
  if (colors.length === 1) {
    return (
      <div
        className="h-4 w-4 shrink-0 rounded-sm border"
        style={{ backgroundColor: colors[0] }}
      />
    );
  }

  if (colors.length <= 4) {
    return (
      <div className="grid h-4 w-4 shrink-0 grid-cols-2 overflow-hidden rounded-sm border">
        {colors.map((color, index) => (
          <div key={`${color}-${index}`} style={{ backgroundColor: color }} />
        ))}
      </div>
    );
  }

  return (
    <div
      className="h-4 w-4 shrink-0 rounded-sm border"
      style={{ background: `linear-gradient(135deg, ${colors.join(", ")})` }}
    />
  );
}

function Circle({ colors }: { colors: string[] }) {
  if (colors.length === 1) {
    return (
      <div
        className="h-4 w-4 shrink-0 rounded-full border"
        style={{ backgroundColor: colors[0] }}
      />
    );
  }

  return (
    <div
      className="h-4 w-4 shrink-0 rounded-full border"
      style={{ background: `conic-gradient(${colors.join(", ")})` }}
    />
  );
}

/** A symbol layer: its icon, its label swatch, or both. */
function Symbol({
  layer,
  color,
  iconUrl,
}: {
  layer: StyleLayer;
  color: string;
  iconUrl: string | null;
}) {
  const textField = layer.layout?.["text-field"];

  return (
    <div className="flex shrink-0 items-center gap-1">
      {iconUrl && (
        // The tenant's own marker artwork, at whatever aspect it was authored.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={iconUrl}
          alt=""
          aria-hidden
          className="h-4 w-4 shrink-0"
          loading="lazy"
        />
      )}

      {!!textField && (
        <span
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border border-gray-300 text-[9px] leading-none text-gray-700"
          style={{ backgroundColor: color }}
          aria-hidden
        >
          aA
        </span>
      )}
    </div>
  );
}
